import React, { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Loader } from 'lucide-react';
import { RootState } from '../../../reducers';
import { fetchFiles } from '../../library/librarySlice';
import { RouteParams } from '../../library/types';
import { getFileUrlForUser } from '../../../lib/routingUtils';
import { updateTitle } from '../../selectedVideo/selectedVideoSlice';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';
import { useSidebar } from '../../../components/ui/sidebar';
import { setDrawerState } from '../dashboardSlice';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../../components/ui/collapsible';
import FileActionMenu from '../../library/files/FileActionMenu';
import FolderTree from './FolderTree';

const FilesList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { teamId, folderId } = useParams<RouteParams>();
  const location = useLocation();

  // Safe usage of useSidebar - it requires SidebarProvider context
  // which may not be available on auth pages (signup/signin)
  let open = false;
  try {
    const sidebar = useSidebar();
    open = sidebar.open;
  } catch {
    // No sidebar context available, component won't render
    return null;
  }

  // Extract fileId from URL path
  const pathParts = location.pathname.split('/');
  const selectedFileId = pathParts.length >= 5 ? pathParts[4] : null;

  const { files, folders, team, globalModals, currentUser } = useSelector(
    (state: RootState) => ({
      files: state.library.files,
      folders: state.library.folders,
      team: state.team,
      globalModals: state.globalModals,
      currentUser: state.settings.user,
    }),
  );

  const [isMobile, setIsMobile] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Load saved state from localStorage
    const saved = localStorage.getItem('filesSectionOpen');
    return saved !== null ? saved === 'false' : false;
  });
  const [hoveredFileId, setHoveredFileId] = useState<string | null>(null);
  const [openMenuFileId, setOpenMenuFileId] = useState<string | null>(null);
  const [renamingFileId, setRenamingFileId] = useState<string | null>(null);
  const [newFileName, setNewFileName] = useState('');
  const [savingFileId, setSavingFileId] = useState<string | null>(null);
  const [folderDropdownOpen, setFolderDropdownOpen] = useState(false);
  const renameInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const selectedTeamId = teamId || team.selectedTeam?.teamId || '';
  const currentFolderId = folderId || 'recents';
  const prevFolderIdRef = React.useRef(currentFolderId);

  // Find current folder name
  const currentFolder = folders.data.find(
    (folder: any) => folder._id === currentFolderId,
  );
  const currentFolderName =
    currentFolder?.name || t('library.recents', 'Recents');

  // Load files when folder changes
  useEffect(() => {
    if (selectedTeamId && currentFolderId) {
      dispatch(
        fetchFiles({
          teamId: selectedTeamId,
          folderId: currentFolderId,
          page: 1,
          limit: 50, // Show latest 50 files
          sortBy: 'createdAt',
          sortOrder: 'desc',
          ...(currentFolderId === 'private' && { includeAllTeams: 'true' }),
        }),
      );
      prevFolderIdRef.current = currentFolderId;
    }
  }, [dispatch, selectedTeamId, currentFolderId]);

  const handleToggleCollapse = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsCollapsed((prev) => {
      const newState = !prev;
      localStorage.setItem('filesSectionOpen', String(!newState));
      return newState;
    });
  }, []);

  const handleStartRename = useCallback(
    (fileId: string, currentName: string) => {
      setRenamingFileId(fileId);
      setNewFileName(currentName);
      setOpenMenuFileId(null);
    },
    [],
  );

  const handleSaveRename = useCallback(async () => {
    if (newFileName.trim() && renamingFileId) {
      setSavingFileId(renamingFileId);
      setRenamingFileId(null);
      try {
        await dispatch(
          updateTitle({
            fileId: renamingFileId,
            title: newFileName.trim(),
          }),
        );
      } finally {
        setSavingFileId(null);
        setNewFileName('');
      }
    } else {
      setRenamingFileId(null);
      setNewFileName('');
    }
  }, [newFileName, renamingFileId, dispatch]);

  const handleCancelRename = useCallback(() => {
    setRenamingFileId(null);
    setNewFileName('');
  }, []);

  // Focus and select input when renaming starts
  useEffect(() => {
    if (renamingFileId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingFileId]);

  // Close folder dropdown when modals open
  useEffect(() => {
    if (globalModals.modalStack.length > 0) {
      setFolderDropdownOpen(false);
    }
  }, [globalModals.modalStack.length]);

  // Close folder dropdown when folder changes (user navigated to a different folder)
  useEffect(() => {
    setFolderDropdownOpen(false);
  }, [currentFolderId]);

  // Only show when sidebar is open
  if (!open) {
    return null;
  }

  // Only show loader if we're loading AND the folder has changed
  const folderHasChanged = prevFolderIdRef.current !== currentFolderId;
  const isLoading = files.status === 'pending' && folderHasChanged;

  return (
    <Collapsible
      open={!isCollapsed}
      onOpenChange={() => setIsCollapsed(!isCollapsed)}
    >
      <div className="flex flex-col h-full">
        {/* Folder Selector with Collapse - Minimal Header Style */}
        <div className="px-2 mb-1 flex items-center gap-1">
          <DropdownMenu
            open={folderDropdownOpen}
            onOpenChange={setFolderDropdownOpen}
          >
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="flex items-center px-2 h-6 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-muted hover:text-gray-700 dark:hover:text-gray-200 rounded-md transition-all duration-200"
              >
                <span className="text-xs font-medium text-muted-foreground dark:text-muted-foreground truncate">
                  {currentFolderName}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[280px] p-2">
              <FolderTree />
            </DropdownMenuContent>
          </DropdownMenu>
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-muted hover:text-gray-700 dark:hover:text-gray-200 rounded-md transition-all duration-200"
              onClick={handleToggleCollapse}
            >
              <ChevronDown
                className={cn(
                  'h-3 w-3 transition-transform duration-200',
                  !isCollapsed && 'rotate-180',
                )}
                strokeWidth={1.75}
              />
            </Button>
          </CollapsibleTrigger>
        </div>

        {/* Files List */}
        <CollapsibleContent>
          <div className="flex-1 overflow-y-auto px-2 py-1">
            {isLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            ) : (
              files.data.length > 0 && (
                <div className="flex flex-col gap-1">
                  {files.data.slice(0, 50).map((file) => {
                    const isLibraryRoute =
                      location.pathname.startsWith('/library/');
                    const isInAccessibleFolder =
                      isLibraryRoute && !!folderId && folderId !== 'recents';
                    const fileUrl = getFileUrlForUser({
                      teamId: selectedTeamId,
                      parentId: file.parentId,
                      fileId: file._id,
                      ownerId: file.ownerId,
                      currentUserId: currentUser?._id,
                      context: isInAccessibleFolder ? 'library' : 'auto',
                      withBasePath: true,
                    });
                    const isHovered = hoveredFileId === file._id;
                    const isMenuOpen = openMenuFileId === file._id;
                    const isSelected = selectedFileId === file._id;
                    const showMenu = isHovered || isMenuOpen;

                    const isRenaming = renamingFileId === file._id;
                    const isSaving = savingFileId === file._id;

                    let fileContent;
                    if (isRenaming) {
                      fileContent = (
                        <div className="rounded-md flex items-center px-3 py-2">
                          <input
                            ref={renameInputRef}
                            type="text"
                            value={newFileName}
                            onChange={(e) => setNewFileName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleSaveRename();
                              } else if (e.key === 'Escape') {
                                e.preventDefault();
                                handleCancelRename();
                              }
                            }}
                            onBlur={handleSaveRename}
                            className="w-full bg-transparent border-0 outline-none text-sm text-gray-700 dark:text-gray-200 focus:ring-0"
                          />
                        </div>
                      );
                    } else if (isSaving) {
                      fileContent = (
                        <div className="flex items-center rounded-md px-3 py-2 text-gray-500 dark:text-gray-400 text-sm">
                          <span className="flex-1 truncate">
                            {newFileName ||
                              file.name ||
                              t('library.untitled', 'Untitled')}
                          </span>
                          <Loader className="h-3 w-3 animate-spin ml-2 flex-shrink-0 text-muted-foreground" />
                        </div>
                      );
                    } else {
                      fileContent = (
                        <>
                          <a
                            href={fileUrl}
                            className={cn(
                              'block rounded-md px-3 py-2 transition-all duration-200',
                              'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-muted hover:text-gray-700 dark:hover:text-gray-200',
                              'text-sm text-left no-underline',
                              showMenu && 'pr-7',
                              isSelected &&
                                'bg-gray-100 dark:bg-muted text-gray-700 dark:text-gray-200',
                            )}
                            onClick={() => {
                              if (isMobile) {
                                dispatch(setDrawerState('closed'));
                              }
                            }}
                          >
                            <span className="block truncate">
                              {file.name || t('library.untitled', 'Untitled')}
                            </span>
                          </a>
                          <div
                            className={cn(
                              'absolute right-1 top-1/2 -translate-y-1/2 transition-opacity duration-200',
                              showMenu || isMobile
                                ? 'opacity-100'
                                : 'opacity-0',
                            )}
                            style={{
                              pointerEvents:
                                showMenu || isMobile ? 'auto' : 'none',
                            }}
                          >
                            <FileActionMenu
                              file={file}
                              onOpenChange={(isOpen) => {
                                setOpenMenuFileId(isOpen ? file._id : null);
                              }}
                              onRename={() =>
                                handleStartRename(file._id, file.name || '')
                              }
                            />
                          </div>
                        </>
                      );
                    }

                    return (
                      <div
                        key={file._id}
                        className="relative"
                        onMouseEnter={() =>
                          !isMobile && setHoveredFileId(file._id)
                        }
                        onMouseLeave={() =>
                          !isMobile && !isMenuOpen && setHoveredFileId(null)
                        }
                      >
                        {fileContent}
                      </div>
                    );
                  })}
                </div>
              )
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

export default FilesList;
